import { useEffect, useState } from "react";
import useUserStore from "@store/useUserStore";
import { getUnreadCount } from "api/user";
import { getCommunityUnreadCount } from "api/community";
import { listMessageRooms } from "api/chat";
import { MESSAGING_ENABLED } from "constants/featureFlags";
import { TokenManager } from "utils/api";

/**
 * 헤더 알림/메시지 뱃지용 미읽음 카운트.
 * 알림 카운트는 예약 + 커뮤니티 합계라 store에 적재하고(다른 화면과 공유),
 * 메시지 카운트는 방 목록 합계라 화면 로컬 state로만 쓴다.
 *
 * @returns {{notificationCount: number, messageUnreadCount: number}}
 */
const useUnreadCounts = () => {
    const userInfo = useUserStore((state) => state.userInfo);
    const notificationCount = useUserStore((state) => state.notificationUnreadCount);
    const setReservationUnreadCount = useUserStore((state) => state.setReservationUnreadCount);
    const setCommunityUnreadCount = useUserStore((state) => state.setCommunityUnreadCount);
    const [messageUnreadCount, setMessageUnreadCount] = useState(0);

    useEffect(() => {
        if (!userInfo) return;
        getUnreadCount()
            .then((res) => {
                setReservationUnreadCount(res?.unread_count ?? 0);
            })
            .catch(() => {});
        getCommunityUnreadCount()
            .then((res) => {
                const count = res?.data?.unread_count ?? res?.unread_count ?? 0;
                setCommunityUnreadCount(count);
            })
            .catch(() => {});
    }, [userInfo, setReservationUnreadCount, setCommunityUnreadCount]);

    useEffect(() => {
        // 메시지 기능 임시 숨김: 진입점이 없으므로 unread 집계 API 호출을 하지 않는다.
        if (!MESSAGING_ENABLED) {
            setMessageUnreadCount(0);
            return;
        }
        const hasAccessToken = Boolean(TokenManager.getAccessToken());
        if (!userInfo || !hasAccessToken) {
            setMessageUnreadCount(0);
            return;
        }
        let isMounted = true;
        listMessageRooms()
            .then((rooms = []) => {
                if (!isMounted) return;
                const count = rooms.reduce((sum, room) => sum + Number(room.unread || 0), 0);
                setMessageUnreadCount(count);
            })
            .catch(() => {
                if (isMounted) setMessageUnreadCount(0);
            });
        return () => {
            isMounted = false;
        };
    }, [userInfo]);

    return { notificationCount, messageUnreadCount };
};

export default useUnreadCounts;
